import {
    createContext, ReactNode, useContext,
    useMemo
} from 'react';
import { LiquidEngine } from '../../utils/liquidEngine';
import { useAuth } from '../AuthContext';
import {
    chatInitialState, ChatState, createChatProvider
} from './chatProvider';
import {
    createDashboardProvider,
    dashboardInitialState, DashboardState
} from './dashboardProvider';
import {
    createModalProvider,
    modalInitialModalState,
    ModalState,
    ModalRender,
} from './modalProvider';
import {
    createPulseProvider,
    pulseInitialState, PulseState
} from './pulseProvider';

// 定义 AdminContext 的数据结构
export interface AdminState {
    chat: ChatState;
    // space: SpaceState;
    pulse: PulseState;
    dashboard: DashboardState;
    modal: ModalState;
    liquidEngine: LiquidEngine;
}

const adminInitialState: AdminState = {
    chat: chatInitialState,
    // space: spaceInitialState,
    pulse: pulseInitialState,
    dashboard: dashboardInitialState,
    modal: modalInitialModalState,
    liquidEngine: new LiquidEngine()
};

const AdminContext = createContext<AdminState>(adminInitialState);

function WorkspaceScopedAdminProviders(props: {
    children: ReactNode;
    workspaceId?: string;
}) {
    // 创建各个模块的 Provider
    const chat = createChatProvider();
    // const space = createSpaceProvider();
    const pulse = createPulseProvider(props.workspaceId);
    const dashboard = createDashboardProvider(props.workspaceId);
    const modal = createModalProvider();

    // 使用 useMemo 缓存 context 值
    const contextValue = useMemo(() => ({
        chat,
        // space,
        pulse,
        dashboard,
        modal,
        liquidEngine: adminInitialState.liquidEngine
    }), [chat, pulse, dashboard, modal]);

    return (
        <AdminContext.Provider value={contextValue}>
            {props.children}
            <ModalRender modalState={modal} />
        </AdminContext.Provider>
    );
}

// 完整的 AdminProvider 包装器
export const AdminProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { currentWorkspace } = useAuth();
    const workspaceScopeKey = currentWorkspace?.id || 'no-workspace';

    return (
        <WorkspaceScopedAdminProviders
            key={workspaceScopeKey}
            workspaceId={currentWorkspace?.id}
        >
            {children}
        </WorkspaceScopedAdminProviders>
    );
};

// 自定义 hook，方便使用
export const useAdminContext = () => useContext(AdminContext);
